'use client'

import { usePathname } from 'next/navigation'
import { useEffect } from 'react'

import { useHeaderNavigationContext } from '@/providers/HeaderNavigation'
import { useLenisContext } from '@/providers/Lenis'

const HEADER_HEIGHT = 64
const MEDIA_PAGE_PATH = '/media-hub'

/**
 * Hook that manages colorInverse state based on scroll position.
 * Sets colorInverse to true when at the top or within the section,
 * and false when scroll position is within HEADER_HEIGHT (64px)
 * of the bottom of the specified height.
 *
 * Only runs on the media page so we never set colorInverse after
 * navigating away (provider keeps colorInverse false when !isMediaPage).
 *
 * @param height - The height of the section (e.g., HeroMedia height)
 */
export function useHeaderColorInverse(height: number) {
  const pathname = usePathname()
  const { scrollPosition } = useLenisContext()
  const { setColorInverse } = useHeaderNavigationContext()
  const isMediaPage = pathname === MEDIA_PAGE_PATH

  useEffect(() => {
    if (!isMediaPage || height === 0) return

    const threshold = height - HEADER_HEIGHT

    if (scrollPosition.y > threshold) {
      setColorInverse(false)
    } else {
      setColorInverse(true)
    }
  }, [pathname, isMediaPage, scrollPosition.y, height, setColorInverse])
}
